/**
 * Lecture et écriture du cookie de langue (DECISIONS §9.3, nom `elemora_locale`).
 *
 * Le cookie est le seul stockage **persistant** du front : il ne contient aucune donnée
 * personnelle, seulement la locale choisie. Le jeton d’accès, lui, ne quitte jamais la mémoire
 * (voir `src/api/session-store.ts`).
 */
import { COOKIE_LOCALE } from '@elemora/shared/constants';

/** Durée de vie du choix de langue : un an, renouvelée à chaque changement. */
const DUREE_COOKIE_JOURS = 365;

/** Lit un cookie par son nom ; `null` s’il est absent. */
export function lireCookie(nom: string): string | null {
  if (typeof document === 'undefined' || document.cookie.length === 0) return null;
  const prefixe = `${nom}=`;
  for (const morceau of document.cookie.split(';')) {
    const valeur = morceau.trim();
    if (valeur.startsWith(prefixe)) {
      return decodeURIComponent(valeur.slice(prefixe.length));
    }
  }
  return null;
}

/**
 * Écrit un cookie de préférence.
 *
 * `SameSite=Lax` suffit : ce cookie n’a aucune valeur d’authentification. `Secure` est ajouté
 * automatiquement lorsque la page est servie en HTTPS (sinon le navigateur refuserait le cookie).
 */
export function ecrireCookie(nom: string, valeur: string, jours = DUREE_COOKIE_JOURS): void {
  if (typeof document === 'undefined') return;
  const expiration = new Date(Date.now() + jours * 24 * 60 * 60 * 1000).toUTCString();
  const securite = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${nom}=${encodeURIComponent(valeur)}; Path=/; Expires=${expiration}; SameSite=Lax${securite}`;
}

/** Enregistre la langue choisie par le joueur. */
export function memoriserLocale(locale: string): void {
  ecrireCookie(COOKIE_LOCALE, locale);
}
